import { createContext, useContext } from "react";

export const LangContext = createContext({ lang: "en", setLang: () => {} });
export const useLang = () => useContext(LangContext);

export const GOOGLE_REVIEWS_URL = "https://share.google/gx3KhwLGeRx4HwOXI";

export const T = {
  en: {
    nav: {
      services: "Services", fleet: "Fleet", destinations: "Destinations", booking: "Booking", contact: "Contact",
      whatsapp: "WhatsApp", call: "Call",
    },
    hero: {
      overline: "Kumar Taxi & Tours · Salzburg, Austria",
      h1a: "Your private chauffeur",
      h1bPre: "in ",
      h1bGold: "Salzburg",
      h1bPost: " & the Alps.",
      sub: "Premium Mercedes taxi, airport transfers and tailor-made day tours to Hallstatt, Zell am See and beyond — reliable, fixed fares, English-speaking driver, 24/7.",
      call: "Call +43 660 2084075",
      whatsapp: "Text Us on WhatsApp",
      explore: "Explore tours",
      badges: ["24/7 Service", "English · Deutsch · Hindi"],
    },
    marquee: [
      "Salzburg City Taxi", "Airport Transfers — SZG · MUC · VIE", "Hallstatt Day Tours",
      "Zell am See", "Sound of Music Route", "VIP Mercedes Fleet", "24/7 WhatsApp Booking",
      "Innsbruck", "Vienna", "Everywhere from Salzburg",
    ],
    services: {
      overline: "What we do",
      h2a: "One call away — ",
      h2b: "every ride",
      h2c: ", every route.",
      cta: "Message Us on WhatsApp",
      cards: [
        {
          title: "City & Station Taxi",
          text: "Fast pick-up anywhere in Salzburg — old town, main station, hotels. Transparent fixed fares and help with your luggage.",
          wa: "Hello Kumar Taxi! I need a taxi in Salzburg. Pickup:",
          testid: "city",
        },
        {
          title: "Airport Transfers",
          text: "Salzburg (SZG), Munich (MUC), Vienna (VIE) & Innsbruck (INN). Meet & greet with name board, live flight tracking, 60 min free waiting.",
          wa: "Hello Kumar Taxi! I need an airport transfer. Flight details:",
          testid: "airport",
        },
        {
          title: "Private Day Tours",
          text: "Hallstatt, Zell am See, Eagle's Nest, Sound of Music spots & the Grossglockner High Alpine Road — your itinerary, your pace.",
          wa: "Hello Kumar Taxi! I'm interested in a private tour to:",
          testid: "tours",
        },
        {
          title: "Ski & Winter Transfers",
          text: "Straight to the slopes — Obertauern, Saalbach-Hinterglemm, Kitzbühel, Zell am See–Kaprun & Kitzsteinhorn glacier. Ski equipment rides free.",
          wa: "Hello Kumar Taxi! I need a ski transfer to:",
          testid: "ski",
        },
      ],
    },
    included: {
      overline: "Included in every ride",
      h2a: "No surprises. ",
      h2b: "Only service.",
      items: [
        { title: "Live flight tracking", text: "We monitor your flight and adjust the pickup automatically.", icon: "plane" },
        { title: "60 min free waiting", text: "At airports & stations — take your time after landing.", icon: "clock" },
        { title: "Free cancellation", text: "Cancel free of charge up to 24 hours before pickup.", icon: "badge" },
        { title: "Fixed price per vehicle", text: "One price for the whole car, never per person. No hidden fees.", icon: "wallet" },
        { title: "Child seats free", text: "Baby seats & boosters provided on request at no cost.", icon: "baby" },
        { title: "No night surcharge", text: "The same fair price at 3 AM as at 3 PM — 24/7.", icon: "moon" },
      ],
    },
    fleet: {
      overline: "The fleet",
      h2a: "Arrive in a ",
      h2b: "Mercedes",
      h2c: ", not just a car.",
      ride: "A ride in a Mercedes, not just a car.",
      multi: {
        title: "1 or several cars?",
        text: "Weddings, groups & events — we coordinate several Mercedes vehicles at once. One message is enough.",
        cta: "Ask for vehicles",
        wa: "Hello Kumar Taxi! We need more than 2 cars for an event / group.",
      },
      side: "Immaculate black Mercedes vehicles, maintained to the highest standard — photographed right here in Salzburg.",
      book: "Book This Ride",
      cars: [
        {
          title: "Mercedes V-Class & Vito",
          tag: "VIP Van · up to 8 Persons",
          specs: ["8 persons", "8 bags", "Climate", "Wi-Fi"],
          wa: "Hello Kumar Taxi! I'd like to book the Mercedes V-Class / Vito (8 persons).",
          testid: "vclass",
        },
        {
          title: "Mercedes E-Class",
          tag: "Business Sedan · 1–4 Persons",
          specs: ["4 persons", "3 bags", "Climate", "Wi-Fi"],
          wa: "Hello Kumar Taxi! I'd like to book the Mercedes E-Class.",
          testid: "eclass",
        },
      ],
    },
    destinations: {
      overline: "Trending & famous tours",
      h2a: "Austria's most beautiful places, ",
      h2b: "chauffeured",
      h2c: ".",
      cta: "Plan this tour",
      cards: [
        {
          name: "Salzburg",
          tagline: "Mozart's baroque masterpiece",
          drive: "From your door",
          duration: "2–4 h city tour",
          spots: "Fortress Hohensalzburg · Mirabell Gardens · Getreidegasse",
          wa: "Hello Kumar Taxi! I'd like a Salzburg city tour.",
          testid: "salzburg",
        },
        {
          name: "Hallstatt",
          tagline: "The postcard village of Austria",
          drive: "~1 h 15 min from Salzburg",
          duration: "Half or full day",
          spots: "Skywalk viewpoint · Salt mines · Lake Hallstatt",
          wa: "Hello Kumar Taxi! I'd like to book a Hallstatt day tour.",
          testid: "hallstatt",
        },
        {
          name: "Zell am See",
          tagline: "Crystal lake between alpine peaks",
          drive: "~1 h 30 min from Salzburg",
          duration: "Half or full day",
          spots: "Lake promenade · Schmittenhöhe · Kitzsteinhorn glacier",
          wa: "Hello Kumar Taxi! I'd like to book a Zell am See tour.",
          testid: "zell",
        },
      ],
    },
    tours: {
      overline: "Tour packages",
      h2a: "Day trips, ",
      h2b: "perfectly planned.",
      sub: "Every tour includes pickup at your address, a private Mercedes and your personal driver — prices on request.",
      priceNote: "Price on request",
      cta: "Request on WhatsApp",
      items: [
        {
          name: "Hallstatt Highlights",
          duration: "Half day · 4–5 h",
          highlights: "Lakeside village · Skywalk 'World Heritage View' · Salt mine panorama",
          wa: "Hello Kumar Taxi! Price for the Hallstatt Highlights tour please.",
          img: "hallstatt_tour",
          testid: "hallstatt",
        },
        {
          name: "Königssee & Eagle's Nest",
          duration: "Full day · 7–8 h",
          highlights: "Emerald fjord lake · Berchtesgaden · Historic mountain retreat",
          wa: "Hello Kumar Taxi! Price for the Königssee & Eagle's Nest tour please.",
          img: "koenigssee",
          testid: "koenigssee",
        },
        {
          name: "Zell am See & Kaprun",
          duration: "Full day · 7–8 h",
          highlights: "Lake promenade · Kitzsteinhorn glacier · High mountain reservoirs",
          wa: "Hello Kumar Taxi! Price for the Zell am See & Kaprun tour please.",
          img: "zell_kaprun",
          testid: "zellkaprun",
        },
        {
          name: "Werfen Ice Caves & Castle",
          duration: "Half day · 4–5 h",
          highlights: "World's largest ice caves · Hohenwerfen fortress · Falconry show",
          wa: "Hello Kumar Taxi! Price for the Werfen Ice Caves & Castle tour please.",
          img: "werfen",
          testid: "werfen",
        },
        {
          name: "Sound of Music Tour",
          duration: "Half day · 3–4 h",
          highlights: "Mirabell Gardens · Leopoldskron Palace · Mondsee wedding church",
          wa: "Hello Kumar Taxi! Price for the Sound of Music tour please.",
          img: "sound_of_music",
          testid: "soundofmusic",
        },
        {
          name: "Munich Day Trip",
          duration: "Full day · 8–9 h",
          highlights: "Marienplatz · English Garden · BMW Welt & Olympic Park",
          wa: "Hello Kumar Taxi! Price for the Munich day trip please.",
          img: "munich",
          testid: "munich",
        },
        {
          name: "Vienna Imperial Day",
          duration: "Full day · 9–10 h",
          highlights: "Schönbrunn Palace · St. Stephen's Cathedral · Ringstrasse",
          wa: "Hello Kumar Taxi! Price for the Vienna day trip please.",
          img: "vienna",
          testid: "vienna",
        },
        {
          name: "Innsbruck & the Nordkette",
          duration: "Full day · 8–9 h",
          highlights: "Colorful old town · Golden Roof · Nordkette cable car views",
          wa: "Hello Kumar Taxi! Price for the Innsbruck day trip please.",
          img: "innsbruck",
          testid: "innsbruck",
        },
        {
          name: "Venice Grand Tour",
          duration: "Full day · 10–12 h",
          highlights: "Grand Canal · Rialto Bridge · St. Mark's Square & gondolas",
          wa: "Hello Kumar Taxi! Price for the Venice grand tour please.",
          img: "venice",
          testid: "venice",
        },
      ],
    },
    reviews: {
      overline: "Guest reviews",
      h2a: "Loved on ",
      h2b: "Google",
      sub: "Real words from real passengers — every ride matters.",
      badgeCount: "32 reviews on Google",
      cards: [
        {
          name: "Google Guest",
          meta: "Google review · 5 stars",
          text: "Best Taxi Company in Salzburg, spoke well English, where is friendly driver, know the area very well, we had a very good experience with him.",
          translation: null,
        },
        {
          name: "Noor Mayal Khanna",
          meta: "Google review · 5 stars",
          text: "Excellent chauffeur, très sympathique et serviable. Nous n'arrivions pas à récupérer notre voiture de location à cause de problèmes administratifs et il est venu à notre secours. Il s'est vraiment mis en quatre.",
          translation: "Excellent chauffeur, very friendly and helpful. We couldn't pick up our rental car due to administrative problems and he came to our rescue. He really went out of his way.",
        },
        {
          name: "Beatrice Perin",
          meta: "Google review · 5 stars",
          text: "Une expérience formidable du début à la fin. Le chauffeur était incroyablement aimable, ponctuel et professionnel. Je recommande vivement ses services à tous ceux qui recherchent un trajet fiable et agréable.",
          translation: "A wonderful experience from start to finish. The chauffeur was incredibly kind, punctual and professional. I highly recommend his services to anyone looking for a reliable and pleasant ride.",
        },
        {
          name: "Raj Bathla",
          meta: "Google review · 5 stars",
          text: "La voiture était impeccable et le chauffeur parlait anglais. Une expérience formidable ! Je recommande vivement.",
          translation: "The car was impeccable and the driver spoke English. A wonderful experience! Highly recommended.",
        },
        {
          name: "Google Guest",
          meta: "Google review · 5 stars",
          from: "en",
          text: "Wonderful experience. He dropped us for Supercup game and was very friendly. We were impressed by him and booked a tour to drive us to Halstatt and back to our hotel. He also drove us through St. Gilgen for no extra cost. Highly recommend!",
          translation: null,
        },
        {
          name: "Google Guest",
          meta: "Google review · 5 stars",
          from: "en",
          text: "A wonderful experience from start to finish. The driver was incredibly kind, punctual and professional. He made the journey easy and stress-free and his friendly attitude made a real difference. I highly recommend his service to anyone looking for a reliable and pleasant taxi experience. Thank you again!",
          translation: null,
        },
      ],
      ctaTitle: "32 guests · 5.0 stars",
      ctaText: "Every review so far is five stars. Read them all on our Google profile.",
      ctaLink: "Open Google reviews",
    },
    routes: {
      overline: "Popular routes",
      h2a: "City to city, ",
      h2b: "door to door.",
      sub: "Fixed fares on all popular connections — ask for your route on WhatsApp.",
      cta: "Get a Fixed Fare",
      items: [
        { to: "Munich Airport", time: "~1 h 45 min" },
        { to: "Vienna", time: "~3 h" },
        { to: "Innsbruck", time: "~2 h" },
        { to: "Prague", time: "~4 h" },
        { to: "Hallstatt", time: "~1 h 15 min" },
        { to: "Zell am See", time: "~1 h 30 min" },
        { to: "Berchtesgaden", time: "~40 min" },
        { to: "Königssee", time: "~45 min" },
      ],
      wa: "Hello Kumar Taxi! Fixed price for Salzburg →",
    },
    faq: {
      overline: "Good to know",
      h2a: "Questions? ",
      h2b: "Answered.",
      items: [
        { q: "How do I book?", a: "Call, WhatsApp or the inquiry form — you get a fixed price and confirmation within minutes." },
        { q: "What happens if my flight is delayed?", a: "Nothing to worry about: we track your flight live and 60 minutes of waiting time at the airport are always free." },
        { q: "Can I cancel free of charge?", a: "Yes — up to 24 hours before pickup, completely free of charge." },
        { q: "Do you provide child seats?", a: "Yes, baby seats and boosters are free on request. Just mention your child's age when booking." },
        { q: "How can I pay?", a: "Cash or card directly in the car — or by bank transfer to our Austrian account (details shown at booking). The fixed price agreed in advance always applies." },
        { q: "Do you also drive to ski resorts?", a: "Of course — Obertauern, Kitzbühel, Saalbach, Zell am See–Kaprun and more. Skis and luggage ride free." },
      ],
      b2bTitle: "Weddings, events & corporate accounts",
      b2bText: "Coordinated shuttles for your guests, teams and special occasions — one call organizes everything.",
      b2bCta: "Ask for a quote",
      b2bWa: "Hello Kumar Taxi! I'd like a quote for an event / corporate shuttle.",
    },
    booking: {
      overline: "Booking inquiry",
      h2a: "Tell us where to — ",
      h2b: "we drive.",
      p: "Send your ride or tour request in under a minute. For the fastest reply, send it straight to our WhatsApp — your details are compiled automatically.",
      boxTitle: "Prefer to talk?",
      boxText: "Call or WhatsApp us anytime — we answer 24/7.",
      bankTitle: "Pay by bank transfer",
      bankText: "Inder Kumar Bathla · IBAN AT08 2011 1502 2641 3101 · BIC GIBAATWWXXX",
      labels: {
        name: "Full name", phone: "Phone / WhatsApp", email: "Email (optional)", pickup: "Pickup location",
        destination: "Destination / tour", datetime: "Date & time", passengers: "Passengers", message: "Special requests",
      },
      placeholders: {
        name: "Your name", phone: "+43 ...", email: "you@example.com", pickup: "Hotel, station, airport...",
        destination: "e.g. Hallstatt, SZG Airport...", message: "Child seat, extra luggage, stops on the way...",
      },
      select: "Select",
      submit: "Send inquiry",
      sms: "Send via SMS",
      whatsapp: "Send via WhatsApp",
      toastSuccess: "Inquiry received! We will confirm shortly — or tap WhatsApp for instant reply.",
      toastError: "Could not send right now — please use WhatsApp instead.",
      toastInfo: "Please add at least your name and pickup location.",
      wa: {
        header: "Hello Kumar Taxi & Tours! New booking inquiry:",
        name: "Name", phone: "Phone", pickup: "Pickup", destination: "Destination / Tour",
        datetime: "Date & Time", passengers: "Passengers", message: "Notes",
      },
    },
    contact: {
      overline: "Contact",
      h2a: "One tap. ",
      h2b: "We're on our way.",
      call: "Tap to call now",
      whatsapp: "Tap to chat on WhatsApp",
      mapTitle: "Based in Salzburg — at home in the Alps",
      mapSub: "Our base and the most requested destinations, at a glance.",
      mapChips: ["Salzburg · Home base", "Hallstatt · ~1 h 15", "Zell am See · ~1 h 30", "Königssee · ~45 min", "Munich Airport · ~1 h 45"],
      facts: [
        "Open 24 hours · 7 days a week",
        "English · Deutsch · हिंदी · ਪੰਜਾਬੀ",
        "Based in Salzburg — serving all of Austria & Bavaria",
      ],
    },
    footer: {
      tagline: "Premium taxi, airport transfers and private tours from Salzburg — driven with pride, priced with honesty.",
      explore: "Explore",
      reach: "Reach us",
      availability: "Salzburg, Austria · Available 24/7",
      rights: "© 2026 Kumar Taxi & Tours. All rights reserved.",
      bankTitle: "Bank transfer",
      bankText: "Inder Kumar Bathla · IBAN AT08 2011 1502 2641 3101 · BIC GIBAATWWXXX",
    },
  },

  de: {
    nav: {
      services: "Leistungen", fleet: "Fuhrpark", destinations: "Ziele", booking: "Buchung", contact: "Kontakt",
      whatsapp: "WhatsApp", call: "Anrufen",
    },
    hero: {
      overline: "Kumar Taxi & Tours · Salzburg, Österreich",
      h1a: "Ihr privater Fahrer",
      h1bPre: "in ",
      h1bGold: "Salzburg",
      h1bPost: " & den Alpen.",
      sub: "Premium-Mercedes-Taxi, Flughafentransfers und maßgeschneiderte Tagestouren nach Hallstatt, Zell am See und darüber hinaus — zuverlässig, Festpreise, 24/7 erreichbar.",
      call: "Anrufen: +43 660 2084075",
      whatsapp: "Per WhatsApp buchen",
      explore: "Touren entdecken",
      badges: ["24/7 Service", "Deutsch · English · Hindi"],
    },
    marquee: [
      "Salzburg Stadt-Taxi", "Flughafentransfer — SZG · MUC · VIE", "Hallstatt Tagestouren",
      "Zell am See", "Sound of Music Tour", "VIP Mercedes Flotte", "24/7 WhatsApp Buchung",
      "Innsbruck", "Wien", "Überall hin ab Salzburg",
    ],
    services: {
      overline: "Unsere Leistungen",
      h2a: "Ein Anruf genügt — ",
      h2b: "jede Fahrt",
      h2c: ", jede Route.",
      cta: "Auf WhatsApp anfragen",
      cards: [
        {
          title: "Stadt- & Bahnhofstaxi",
          text: "Schnelle Abholung überall in Salzburg — Altstadt, Hauptbahnhof, Hotels. Transparente Festpreise und Hilfe mit Ihrem Gepäck.",
          wa: "Hallo Kumar Taxi! Ich brauche ein Taxi in Salzburg. Abholort:",
          testid: "city",
        },
        {
          title: "Flughafentransfer",
          text: "Salzburg (SZG), München (MUC), Wien (VIE) & Innsbruck (INN). Empfang mit Namensschild, Live-Flugüberwachung, 60 Min. Gratis-Wartezeit.",
          wa: "Hallo Kumar Taxi! Ich brauche einen Flughafentransfer. Flugdetails:",
          testid: "airport",
        },
        {
          title: "Private Tagestouren",
          text: "Hallstatt, Zell am See, Kehlsteinhaus, Sound-of-Music-Orte & Großglockner-Hochalpenstraße — Ihre Route, Ihr Tempo.",
          wa: "Hallo Kumar Taxi! Ich interessiere mich für eine private Tour nach:",
          testid: "tours",
        },
        {
          title: "Ski- & Wintertransfer",
          text: "Direkt zur Piste — Obertauern, Saalbach-Hinterglemm, Kitzbühel, Zell am See–Kaprun & Kitzsteinhorn-Gletscher. Skiausrüstung fährt kostenlos mit.",
          wa: "Hallo Kumar Taxi! Ich brauche einen Ski-Transfer nach:",
          testid: "ski",
        },
      ],
    },
    included: {
      overline: "In jeder Fahrt inklusive",
      h2a: "Keine Überraschungen. ",
      h2b: "Nur Service.",
      items: [
        { title: "Live-Flugüberwachung", text: "Wir verfolgen Ihren Flug live und passen die Abholung automatisch an.", icon: "plane" },
        { title: "60 Min. Gratis-Wartezeit", text: "Am Flughafen & Bahnhof — lassen Sie sich nach der Landung Zeit.", icon: "clock" },
        { title: "Kostenlose Stornierung", text: "Bis 24 Stunden vor der Abholung komplett kostenfrei stornierbar.", icon: "badge" },
        { title: "Festpreis pro Fahrzeug", text: "Ein Preis fürs ganze Auto, nie pro Person. Keine versteckten Kosten.", icon: "wallet" },
        { title: "Kindersitze gratis", text: "Babyschalen & Sitzerhöhungen auf Wunsch kostenlos dabei.", icon: "baby" },
        { title: "Kein Nachtzuschlag", text: "Der gleiche faire Preis um 3 Uhr nachts wie um 15 Uhr — 24/7.", icon: "moon" },
      ],
    },
    fleet: {
      overline: "Die Flotte",
      h2a: "Ankommen in einem ",
      h2b: "Mercedes",
      h2c: ", nicht nur in einem Auto.",
      ride: "Eine Fahrt im Mercedes, nicht nur ein Auto.",
      multi: {
        title: "Mehr als 2 Autos bestellen?",
        text: "Hochzeiten, Gruppen & Events — wir koordinieren mehrere Mercedes gleichzeitig. Eine Nachricht genügt.",
        cta: "Fahrzeuge anfragen",
        wa: "Hallo Kumar Taxi! Wir benötigen mehr als 2 Autos für einen Event / eine Gruppe.",
      },
      side: "Gepflegte schwarze Mercedes-Fahrzeuge, gewartet nach höchstem Standard — fotografiert direkt hier in Salzburg.",
      book: "Dieses Auto buchen",
      cars: [
        {
          title: "Mercedes V-Class & Vito",
          tag: "VIP Van · bis zu 8 Personen",
          specs: ["8 Personen", "8 Koffer", "Klima", "WLAN"],
          wa: "Hallo Kumar Taxi! Ich möchte den Mercedes V-Class / Vito (8 Personen) buchen.",
          testid: "vclass",
        },
        {
          title: "Mercedes E-Class",
          tag: "Business-Limousine · 1–4 Personen",
          specs: ["4 Personen", "3 Koffer", "Klima", "WLAN"],
          wa: "Hallo Kumar Taxi! Ich möchte die Mercedes E-Class buchen.",
          testid: "eclass",
        },
      ],
    },
    destinations: {
      overline: "Angesagte & berühmte Touren",
      h2a: "Österreichs schönste Orte, ",
      h2b: "chauffiert",
      h2c: ".",
      cta: "Tour planen",
      cards: [
        {
          name: "Salzburg",
          tagline: "Mozarts barockes Meisterwerk",
          drive: "Ab Ihrer Haustür",
          duration: "2–4 Std. Stadttour",
          spots: "Festung Hohensalzburg · Mirabellgarten · Getreidegasse",
          wa: "Hallo Kumar Taxi! Ich möchte eine Salzburg Stadttour.",
          testid: "salzburg",
        },
        {
          name: "Hallstatt",
          tagline: "Das Postkartendorf Österreichs",
          drive: "~1 Std. 15 Min. ab Salzburg",
          duration: "Halb- oder ganztägig",
          spots: "Skywalk Aussichtsplattform · Salzbergwerk · Hallstätter See",
          wa: "Hallo Kumar Taxi! Ich möchte eine Hallstatt Tagestour buchen.",
          testid: "hallstatt",
        },
        {
          name: "Zell am See",
          tagline: "Kristallklarer See zwischen Alpengipfeln",
          drive: "~1 Std. 30 Min. ab Salzburg",
          duration: "Halb- oder ganztägig",
          spots: "Seepromenade · Schmittenhöhe · Kitzsteinhorn-Gletscher",
          wa: "Hallo Kumar Taxi! Ich möchte eine Zell am See Tour buchen.",
          testid: "zell",
        },
      ],
    },
    tours: {
      overline: "Tour-Pakete",
      h2a: "Tagesausflüge, ",
      h2b: "perfekt geplant.",
      sub: "Jede Tour inklusive Abholung an Ihrer Adresse, privatem Mercedes und persönlichem Fahrer — Preise auf Anfrage.",
      priceNote: "Preis auf Anfrage",
      cta: "Auf WhatsApp anfragen",
      items: [
        {
          name: "Hallstatt Highlights",
          duration: "Halbtags · 4–5 Std.",
          highlights: "Seedorf · Skywalk 'Welterbeblick' · Salzbergwerk-Panorama",
          wa: "Hallo Kumar Taxi! Preis für die Hallstatt Highlights Tour bitte.",
          img: "hallstatt_tour",
          testid: "hallstatt",
        },
        {
          name: "Königssee & Kehlsteinhaus",
          duration: "Ganztags · 7–8 Std.",
          highlights: "Smaragdgrüner Fjordsee · Berchtesgaden · Historisches Kehlsteinhaus",
          wa: "Hallo Kumar Taxi! Preis für die Königssee & Kehlsteinhaus Tour bitte.",
          img: "koenigssee",
          testid: "koenigssee",
        },
        {
          name: "Zell am See & Kaprun",
          duration: "Ganztags · 7–8 Std.",
          highlights: "Seepromenade · Kitzsteinhorn-Gletscher · Hochgebirgsstauseen",
          wa: "Hallo Kumar Taxi! Preis für die Zell am See & Kaprun Tour bitte.",
          img: "zell_kaprun",
          testid: "zellkaprun",
        },
        {
          name: "Eisriesenwelt & Burg Hohenwerfen",
          duration: "Halbtags · 4–5 Std.",
          highlights: "Größte Eishöhlen der Welt · Festung Hohenwerfen · Greifvogelschau",
          wa: "Hallo Kumar Taxi! Preis für die Eisriesenwelt & Burg Hohenwerfen Tour bitte.",
          img: "werfen",
          testid: "werfen",
        },
        {
          name: "Sound of Music Tour",
          duration: "Halbtags · 3–4 Std.",
          highlights: "Mirabellgarten · Schloss Leopoldskron · Mondseer Hochzeitskirche",
          wa: "Hallo Kumar Taxi! Preis für die Sound of Music Tour bitte.",
          img: "sound_of_music",
          testid: "soundofmusic",
        },
        {
          name: "München Tagesausflug",
          duration: "Ganztags · 8–9 Std.",
          highlights: "Marienplatz · Englischer Garten · BMW Welt & Olympiapark",
          wa: "Hallo Kumar Taxi! Preis für den München Tagesausflug bitte.",
          img: "munich",
          testid: "munich",
        },
        {
          name: "Wien Kaiserlicher Tag",
          duration: "Ganztags · 9–10 Std.",
          highlights: "Schloss Schönbrunn · Stephansdom · Ringstraße",
          wa: "Hallo Kumar Taxi! Preis für den Wien Tagesausflug bitte.",
          img: "vienna",
          testid: "vienna",
        },
        {
          name: "Innsbruck & die Nordkette",
          duration: "Ganztags · 8–9 Std.",
          highlights: "Bunte Altstadt · Goldenes Dachl · Nordketten-Panorama",
          wa: "Hallo Kumar Taxi! Preis für den Innsbruck Tagesausflug bitte.",
          img: "innsbruck",
          testid: "innsbruck",
        },
        {
          name: "Venedig Grand Tour",
          duration: "Ganztags · 10–12 Std.",
          highlights: "Canal Grande · Rialtobrücke · Markusplatz & Gondeln",
          wa: "Hallo Kumar Taxi! Preis für die Venedig Grand Tour bitte.",
          img: "venice",
          testid: "venice",
        },
      ],
    },
    reviews: {
      overline: "Gästebewertungen",
      h2a: "Top bewertet auf ",
      h2b: "Google",
      sub: "Echte Worte von echten Fahrgästen — jede Fahrt zählt.",
      badgeCount: "32 Bewertungen auf Google",
      cards: [
        {
          name: "Google-Gast",
          meta: "Google-Bewertung · 5 Sterne",
          text: "Best Taxi Company in Salzburg, spoke well English, where is friendly driver, know the area very well, we had a very good experience with him.",
          translation: "Bestes Taxi-Unternehmen in Salzburg, spricht sehr gut Englisch, sehr freundlicher Fahrer, kennt die Gegend sehr gut — wir hatten eine sehr gute Erfahrung mit ihm.",
        },
        {
          name: "Noor Mayal Khanna",
          meta: "Google-Bewertung · 5 Sterne",
          text: "Excellent chauffeur, très sympathique et serviable. Nous n'arrivions pas à récupérer notre voiture de location à cause de problèmes administratifs et il est venu à notre secours. Il s'est vraiment mis en quatre.",
          translation: "Ausgezeichneter Chauffeur, sehr sympathisch und hilfsbereit. Wir konnten unseren Mietwagen wegen administrativer Probleme nicht abholen — und er kam uns zur Rettung. Er hat sich wirklich ins Zeug gelegt.",
        },
        {
          name: "Beatrice Perin",
          meta: "Google-Bewertung · 5 Sterne",
          text: "Une expérience formidable du début à la fin. Le chauffeur était incroyablement aimable, ponctuel et professionnel. Je recommande vivement ses services à tous ceux qui recherchent un trajet fiable et agréable.",
          translation: "Eine großartige Erfahrung von Anfang bis Ende. Der Chauffeur war unglaublich freundlich, pünktlich und professionell. Ich empfehle ihn jedem, der eine zuverlässige und angenehme Fahrt sucht.",
        },
        {
          name: "Raj Bathla",
          meta: "Google-Bewertung · 5 Sterne",
          text: "La voiture était impeccable et le chauffeur parlait anglais. Une expérience formidable ! Je recommande vivement.",
          translation: "Das Auto war makellos und der Fahrer sprach Englisch. Eine großartige Erfahrung! Sehr zu empfehlen.",
        },
        {
          name: "Google-Gast",
          meta: "Google-Bewertung · 5 Sterne",
          from: "en",
          text: "Wonderful experience. He dropped us for Supercup game and was very friendly. We were impressed by him and booked a tour to drive us to Halstatt and back to our hotel. He also drove us through St. Gilgen for no extra cost. Highly recommend!",
          translation: "Großartige Erfahrung. Er hat uns zum Supercup-Spiel gebracht und war sehr freundlich. Wir waren so beeindruckt, dass wir gleich eine Tour nach Hallstatt und zurück zum Hotel gebucht haben. Er fuhr uns sogar ohne Aufpreis durch St. Gilgen. Sehr zu empfehlen!",
        },
        {
          name: "Google-Gast",
          meta: "Google-Bewertung · 5 Sterne",
          from: "en",
          text: "A wonderful experience from start to finish. The driver was incredibly kind, punctual and professional. He made the journey easy and stress-free and his friendly attitude made a real difference. I highly recommend his service to anyone looking for a reliable and pleasant taxi experience. Thank you again!",
          translation: "Eine großartige Erfahrung von Anfang bis Ende. Der Fahrer war unglaublich freundlich, pünktlich und professionell. Er machte die Fahrt einfach und stressfrei — seine freundliche Art machte einen echten Unterschied. Ich empfehle ihn jedem, der ein zuverlässiges und angenehmes Taxi sucht. Nochmals vielen Dank!",
        },
      ],
      ctaTitle: "32 Gäste · 5,0 Sterne",
      ctaText: "Jede bisherige Bewertung hat fünf Sterne. Lesen Sie alle auf unserem Google-Profil.",
      ctaLink: "Google-Bewertungen öffnen",
    },
    routes: {
      overline: "Beliebte Strecken",
      h2a: "Von Stadt zu Stadt, ",
      h2b: "von Tür zu Tür.",
      sub: "Festpreise auf allen beliebten Verbindungen — fragen Sie Ihre Route per WhatsApp an.",
      cta: "Festpreis anfragen",
      items: [
        { to: "Flughafen München", time: "~1 Std. 45 Min." },
        { to: "Wien", time: "~3 Std." },
        { to: "Innsbruck", time: "~2 Std." },
        { to: "Prag", time: "~4 Std." },
        { to: "Hallstatt", time: "~1 Std. 15 Min." },
        { to: "Zell am See", time: "~1 Std. 30 Min." },
        { to: "Berchtesgaden", time: "~40 Min." },
        { to: "Königssee", time: "~45 Min." },
      ],
      wa: "Hallo Kumar Taxi! Festpreis für Salzburg →",
    },
    faq: {
      overline: "Gut zu wissen",
      h2a: "Fragen? ",
      h2b: "Beantwortet.",
      items: [
        { q: "Wie buche ich?", a: "Anruf, WhatsApp oder Anfrageformular — Sie erhalten innerhalb von Minuten einen Festpreis und eine Bestätigung." },
        { q: "Was passiert bei Flugverspätung?", a: "Kein Problem: Wir verfolgen Ihren Flug live und 60 Minuten Wartezeit am Flughafen sind immer gratis." },
        { q: "Kann ich kostenlos stornieren?", a: "Ja — bis 24 Stunden vor der Abholung komplett kostenfrei." },
        { q: "Gibt es Kindersitze?", a: "Ja, Babyschalen und Sitzerhöhungen sind auf Wunsch kostenlos. Nennen Sie bei der Buchung einfach das Alter Ihres Kindes." },
        { q: "Wie kann ich bezahlen?", a: "Bar oder mit Karte direkt im Auto — oder per Überweisung auf unser österreichisches Konto (Daten bei der Buchung). Es gilt immer der vorher vereinbarte Festpreis." },
        { q: "Fahren Sie auch in Skigebiete?", a: "Selbstverständlich — Obertauern, Kitzbühel, Saalbach, Zell am See–Kaprun und mehr. Ski und Gepäck fahren kostenlos mit." },
      ],
      b2bTitle: "Hochzeiten, Events & Firmenkonten",
      b2bText: "Koordinierte Shuttles für Ihre Gäste, Teams und besondere Anlässe — ein Anruf organisiert alles.",
      b2bCta: "Angebot anfragen",
      b2bWa: "Hallo Kumar Taxi! Ich hätte gern ein Angebot für einen Event- / Firmen-Shuttle.",
    },
    booking: {
      overline: "Buchungsanfrage",
      h2a: "Sagen Sie uns wohin — ",
      h2b: "wir fahren.",
      p: "Senden Sie Ihre Fahrt- oder Touranfrage in unter einer Minute. Für die schnellste Antwort senden Sie sie direkt an unser WhatsApp — Ihre Angaben werden automatisch übernommen.",
      boxTitle: "Lieber persönlich?",
      boxText: "Rufen Sie an oder schreiben Sie per WhatsApp — wir sind 24/7 erreichbar.",
      bankTitle: "Zahlung per Überweisung",
      bankText: "Inder Kumar Bathla · IBAN AT08 2011 1502 2641 3101 · BIC GIBAATWWXXX",
      labels: {
        name: "Vollständiger Name", phone: "Telefon / WhatsApp", email: "E-Mail (optional)", pickup: "Abholort",
        destination: "Ziel / Tour", datetime: "Datum & Uhrzeit", passengers: "Personen", message: "Besondere Wünsche",
      },
      placeholders: {
        name: "Ihr Name", phone: "+43 ...", email: "ihre@email.at", pickup: "Hotel, Bahnhof, Flughafen...",
        destination: "z.B. Hallstatt, Flughafen SZG...", message: "Kindersitz, Extragepäck, Zwischenstopps...",
      },
      select: "Bitte wählen",
      submit: "Anfrage senden",
      sms: "Per SMS senden",
      whatsapp: "Per WhatsApp senden",
      toastSuccess: "Anfrage erhalten! Wir bestätigen in Kürze — oder tippen Sie auf WhatsApp für eine sofortige Antwort.",
      toastError: "Senden momentan nicht möglich — bitte nutzen Sie WhatsApp.",
      toastInfo: "Bitte geben Sie mindestens Ihren Namen und Abholort an.",
      wa: {
        header: "Hallo Kumar Taxi & Tours! Neue Buchungsanfrage:",
        name: "Name", phone: "Telefon", pickup: "Abholort", destination: "Ziel / Tour",
        datetime: "Datum & Uhrzeit", passengers: "Personen", message: "Bemerkungen",
      },
    },
    contact: {
      overline: "Kontakt",
      h2a: "Ein Klick. ",
      h2b: "Wir sind unterwegs.",
      call: "Jetzt anrufen",
      whatsapp: "Auf WhatsApp chatten",
      mapTitle: "Ansässig in Salzburg — zuhause in den Alpen",
      mapSub: "Unsere Basis und die meistgewünschten Ziele auf einen Blick.",
      mapChips: ["Salzburg · Homebase", "Hallstatt · ~1 Std. 15", "Zell am See · ~1 Std. 30", "Königssee · ~45 Min.", "Flughafen München · ~1 Std. 45"],
      facts: [
        "24 Stunden · 7 Tage die Woche geöffnet",
        "Deutsch · English · हिंदी · ਪੰਜਾਬੀ",
        "Ansässig in Salzburg — ganz Österreich & Bayern",
      ],
    },
    footer: {
      tagline: "Premium-Taxi, Flughafentransfers und private Touren ab Salzburg — mit Stolz gefahren, ehrlich kalkuliert.",
      explore: "Entdecken",
      reach: "Kontakt",
      availability: "Salzburg, Österreich · 24/7 erreichbar",
      rights: "© 2026 Kumar Taxi & Tours. Alle Rechte vorbehalten.",
      bankTitle: "Überweisung",
      bankText: "Inder Kumar Bathla · IBAN AT08 2011 1502 2641 3101 · BIC GIBAATWWXXX",
    },
  },
};
